"use client";

import React, { useRef } from 'react';
import Image from 'next/image';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '@/context/LanguageContext';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i: number = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const fadeLeft = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const fadeRight = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function AIAgentsSection() {
  const { t } = useLanguage();
  const sectionRef = useRef<HTMLDivElement>(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.15 });

  const agentCapabilities = [
    { label: t('agents_cap1'), icon: '🔍', color: '#FF912E' },
    { label: t('agents_cap2'), icon: '📊', color: '#4A9EFF' },
    { label: t('agents_cap3'), icon: '⚡', color: '#FF912E' },
    { label: t('agents_cap4'), icon: '🧠', color: '#4A9EFF' },
    { label: t('agents_cap5'), icon: '💬', color: '#FF912E' },
    { label: t('agents_cap6'), icon: '📋', color: '#4A9EFF' },
  ];

  const nodes = [
    { top: '5%', left: '50%', translate: '-50%', label: t('agents_node1'), icon: '🔍' },
    { top: '22%', right: '5%', label: t('agents_node2'), icon: '📊' },
    { top: '60%', right: '5%', label: t('agents_node3'), icon: '🧠' },
    { bottom: '5%', left: '50%', translate: '-50%', label: t('agents_node4'), icon: '💬' },
    { top: '60%', left: '5%', label: t('agents_node5'), icon: '📋' },
    { top: '22%', left: '5%', label: t('agents_node6'), icon: '⚡' },
  ];

  return (
    <section id="ai-agents" ref={sectionRef} className="py-28 relative overflow-hidden bg-[#07111F]">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(255,145,46,0.2)] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(139,158,192,0.15)] to-transparent" />
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(30,58,95,0.25) 0%, transparent 70%)' }}
      />
      <div className="max-w-container mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <motion.div
            variants={fadeLeft}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
          >
            <div className="section-label mb-4">{t('agents_label')}</div>
            <h2 className="font-heading text-headline font-bold text-white mb-6">
              {t('agents_headline_1')}{' '}
              <span className="text-gradient-orange">{t('agents_headline_accent')}</span>,<br />
              {t('agents_headline_2')}
            </h2>
            <p className="text-muted text-lg leading-relaxed mb-8">{t('agents_sub')}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {agentCapabilities.map((cap, i) => (
                <motion.div
                  key={cap.label}
                  variants={fadeUp}
                  initial="hidden"
                  animate={inView ? 'visible' : 'hidden'}
                  custom={i + 1}
                  className="flex items-center gap-3 p-3 rounded-xl bg-[rgba(13,30,53,0.6)] border border-[rgba(139,158,192,0.1)] hover:border-[rgba(255,145,46,0.25)] transition-all group cursor-default"
                >
                  <span className="text-xl">{cap.icon}</span>
                  <span className="text-sm font-medium text-white group-hover:text-primary transition-colors">{cap.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right: Node network */}
          <motion.div
            variants={fadeRight}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            className="relative"
          >
            <div className="relative w-full aspect-square max-w-[680px] mx-auto">
              <div className="absolute inset-8 rounded-full border border-[rgba(139,158,192,0.08)]" />
              <div className="absolute inset-16 rounded-full border border-[rgba(255,145,46,0.06)]" />

              {/* Central AI node */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
                <div className="relative">
                  <div className="w-36 h-36 rounded-2xl flex items-center justify-center overflow-hidden p-3">
                    <Image
                      src="/assets/images/positivo---color-1773188764124.png"
                      alt="Devros AI Logo"
                      width={120}
                      height={120}
                      className="object-contain w-full h-full"
                    />
                  </div>
                  <div className="absolute -inset-3 rounded-3xl border border-[rgba(255,145,46,0.2)] animate-pulse-glow" style={{ animationDelay: '0.5s' }} />
                </div>
              </div>

              {/* SVG connection lines */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 680 680" fill="none">
                {[
                  { x1: 340, y1: 340, x2: 340, y2: 80 },
                  { x1: 340, y1: 340, x2: 570, y2: 200 },
                  { x1: 340, y1: 340, x2: 570, y2: 480 },
                  { x1: 340, y1: 340, x2: 340, y2: 600 },
                  { x1: 340, y1: 340, x2: 110, y2: 480 },
                  { x1: 340, y1: 340, x2: 110, y2: 200 },
                ].map((line, i) => (
                  <line
                    key={i}
                    x1={line.x1} y1={line.y1} x2={line.x2} y2={line.y2}
                    stroke="rgba(255, 145, 46, 0.25)"
                    strokeWidth="1"
                    strokeDasharray="6 4"
                    className="node-line"
                    style={{ animationDelay: `${i * 0.3}s` }}
                  />
                ))}
              </svg>

              {/* Peripheral nodes */}
              {nodes.map((node, i) => (
                <div
                  key={node.label}
                  className="absolute z-10"
                  style={{
                    top: node.top,
                    bottom: node.bottom,
                    left: node.left,
                    right: node.right,
                    transform: node.translate ? `translateX(${node.translate})` : undefined,
                  }}
                >
                  <div
                    className="w-16 h-16 rounded-xl card-glass flex flex-col items-center justify-center gap-0.5 hover:border-[rgba(255,145,46,0.4)] transition-all cursor-default"
                    style={{ animationDelay: `${i * 0.5}s` }}
                  >
                    <span className="text-xl">{node.icon}</span>
                    <span className="text-[10px] text-muted font-medium">{node.label}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}